import { API_CONFIG } from "../config/api";
import {
	type CookieConsentResult,
	type CookieConsentState,
	NonEssentialVendors,
	type PolicyCategory,
	type VendorCatalog,
	type VendorEntry,
} from "../types/cookieConsent";

const SDK_URL =
	"https://cookie.access-evo.com/consent/access-cookie-policies-js/";

const SCRIPT_ID = "access-cookie-policies-sdk";

// TODO: Confirm the canonical Access privacy-notice URL with product/legal
const ACCESS_PRIVACY_URL =
	"https://www.theaccessgroup.com/en-gb/legal/privacy-notice/";

function isLocalDevelopment(): boolean {
	const hostname = window.location.hostname;
	return hostname === "localhost" || hostname === "127.0.0.1";
}

export function buildVendorCatalog(): VendorCatalog {
	const essential: PolicyCategory = {
		policyName: "essential",
		vendors: [
			{
				name: "Authentication (OIDC)",
				description:
					"Stores the authenticated user session in sessionStorage (oidc.user:*) to maintain login state.",
				vendorUrl: ACCESS_PRIVACY_URL,
				partyType: "First Party",
			},
			{
				name: "App Mode",
				description:
					"Remembers the selected application mode (demo/live) in localStorage (app-mode).",
				vendorUrl: ACCESS_PRIVACY_URL,
				partyType: "First Party",
			},
			{
				name: "Onboarding State",
				description:
					"Tracks onboarding progress in localStorage (onboarding_*) so the assistant resumes where you left off.",
				vendorUrl: ACCESS_PRIVACY_URL,
				partyType: "First Party",
			},
			{
				name: "User Preferences",
				description:
					"Persists per-user display preferences (theme, landing page) in localStorage (cashflow_user_preferences).",
				vendorUrl: ACCESS_PRIVACY_URL,
				partyType: "First Party",
			},
			{
				name: "Sidebar State",
				description:
					"Stores sidebar open/collapsed state in a cookie (sidebar_state) to preserve layout across pages.",
				vendorUrl: ACCESS_PRIVACY_URL,
				partyType: "First Party",
			},
			{
				name: "Terminology",
				description:
					"Caches the organisation's custom terminology configuration in sessionStorage (system-terminology-config).",
				vendorUrl: ACCESS_PRIVACY_URL,
				partyType: "First Party",
			},
			{
				name: "Jurisdiction Selection",
				description:
					"Stores selected jurisdiction and settings in localStorage (cashflow_jurisdictions, cashflow_settings, cashflow_selected_jurisdiction_id) for multi-jurisdiction support.",
				vendorUrl: ACCESS_PRIVACY_URL,
				partyType: "First Party",
			},
		],
	};

	const analytics: PolicyCategory = {
		policyName: "analytics",
		vendors: [
			{
				name: NonEssentialVendors.WOOTRIC,
				key: NonEssentialVendors.WOOTRIC,
				description:
					"In-app NPS survey tool used to collect user satisfaction feedback.",
				vendorUrl: "https://wootric.com/privacy-policy/",
				partyType: "Third Party",
			},
			{
				name: NonEssentialVendors.FEATURE_FEEDBACK_COLLECTOR,
				key: NonEssentialVendors.FEATURE_FEEDBACK_COLLECTOR,
				description:
					"Collects per-feature sentiment feedback to help prioritise product improvements.",
				vendorUrl: ACCESS_PRIVACY_URL,
				partyType: "First Party",
			},
			{
				name: NonEssentialVendors.INTERCOM,
				key: NonEssentialVendors.INTERCOM,
				description:
					"Customer messaging platform for in-app support and communication (live mode only).",
				vendorUrl: "https://www.intercom.com/legal/privacy",
				partyType: "First Party",
			},
		],
	};

	return { essential, analytics };
}

function buildPartyEntry(vendor: VendorEntry, policyName: string) {
	return {
		partyName: vendor.name,
		partyType: vendor.partyType,
		policies: [
			{
				policyName,
				vendorName: vendor.name,
				vendorUrl: vendor.vendorUrl,
				description: vendor.description,
			},
		],
	};
}

export function buildParties(catalog: VendorCatalog) {
	const parties: Array<{
		partyName: string;
		partyType: "First Party" | "Third Party";
		policies: Array<{
			policyName: string;
			vendorName: string;
			vendorUrl: string;
			description: string;
		}>;
	}> = [];

	for (const vendor of catalog.essential.vendors) {
		parties.push(buildPartyEntry(vendor, "essential & security"));
	}

	for (const vendor of catalog.analytics.vendors) {
		parties.push(buildPartyEntry(vendor, catalog.analytics.policyName));
	}

	return parties;
}

function loadScript(): Promise<void> {
	return new Promise((resolve, reject) => {
		if (document.getElementById(SCRIPT_ID)) {
			resolve();
			return;
		}

		const script = document.createElement("script");
		script.id = SCRIPT_ID;
		script.src = SDK_URL;
		script.async = true;
		script.onload = () => resolve();
		script.onerror = () =>
			reject(new Error("Failed to load Access Cookie Policies SDK"));
		document.head.appendChild(script);
	});
}

function waitForSdk(attempts = 0): Promise<typeof window.AccessCookiePolicies> {
	const maxAttempts = 20;
	const retryDelay = 500;

	return new Promise((resolve, reject) => {
		if (window.AccessCookiePolicies?.SDK) {
			resolve(window.AccessCookiePolicies);
			return;
		}

		if (attempts >= maxAttempts) {
			reject(
				new Error("Access Cookie Policies SDK did not initialise in time"),
			);
			return;
		}

		setTimeout(() => {
			waitForSdk(attempts + 1).then(resolve, reject);
		}, retryDelay);
	});
}

function createGrantAllConsent(): CookieConsentResult {
	const consent: CookieConsentState = {
		"essential & security": true,
		analytics: true,
	};
	return {
		consent,
		isGranted: (policyName: string) => consent[policyName] ?? true,
	};
}

interface LoadCookieSdkOptions {
	policyUrl: string;
}

export async function loadCookieSdk(
	options: LoadCookieSdkOptions,
): Promise<CookieConsentResult> {
	if (isLocalDevelopment()) {
		return createGrantAllConsent();
	}

	await loadScript();
	const acp = await waitForSdk();

	if (!acp?.SDK) {
		throw new Error("Access Cookie Policies SDK not available");
	}

	const catalog = buildVendorCatalog();
	const parties = buildParties(catalog);
	const policies = [
		{ policyName: "essential & security" },
		{ policyName: catalog.analytics.policyName },
	];

	const result = await acp.SDK.tabInitialise({
		config: {
			policies,
			parties,
			applicationId: API_CONFIG.cookieApplicationId,
			policyVersion: API_CONFIG.cookiePolicyVersion,
			policyUrl: options.policyUrl,
		},
		language: "en",
	});

	return result;
}

/**
 * Reads the consent cookie, handling .NET-style chunking where a cookie named
 * `access-evo-cc.<experience>` may be split into `C1`, `C2`, etc. suffixes.
 */
export function readConsentCookie(
	experience: string,
): CookieConsentState | null {
	const baseName = `access-evo-cc.${experience}`;
	const cookies = document.cookie
		.split(";")
		.reduce<Record<string, string>>((acc, c) => {
			const [key, ...rest] = c.trim().split("=");
			if (key) {
				acc[key] = rest.join("=");
			}
			return acc;
		}, {});

	// Try single cookie first
	if (cookies[baseName]) {
		try {
			return JSON.parse(
				decodeURIComponent(cookies[baseName]),
			) as CookieConsentState;
		} catch {
			return null;
		}
	}

	// Try chunked cookies (C1, C2, ...)
	let assembled = "";
	let chunk = 1;
	while (cookies[`${baseName}C${chunk}`] !== undefined) {
		assembled += cookies[`${baseName}C${chunk}`];
		chunk++;
	}

	if (!assembled) {
		return null;
	}

	try {
		return JSON.parse(decodeURIComponent(assembled)) as CookieConsentState;
	} catch {
		return null;
	}
}
